import { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SelectChangeEvent } from '@mui/material/Select';
import { APIHOST } from '../../../common';
import { Conference } from '../../../types/conference';
import { useApp } from '../../../components/AppContext';



interface AvailabilityReportProps {
  conferences: Conference[];
}

export default function AvailabilityReportText({ conferences }: AvailabilityReportProps) {
  const [selectedConference, setSelectedConference] = useState('');
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { setSnackbarInput } = useApp();

  const loadAvailability = (conferenceId: string) => {
    setLoading(true);
    fetch(`${APIHOST}/api/conferences/${conferenceId}/availability`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
    })
      .then(response => response.json())
      .then(data => {
        setReportData(data);
        setSnackbarInput({message: 'Conference availability data fetched successfully.'});
      })
      .catch(error => {
        console.error('Error fetching conference availability:', error);
        setSnackbarInput({message: 'Error fetching conference availability.', severity: 'error'});
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleConferenceChange = (event: SelectChangeEvent) => {
    setSelectedConference(event.target.value);
    loadAvailability(event.target.value);
  };

  // Process report data for Chart.js - one chart per hotel
  const getHotelsFromData = () => {
    if (!reportData) return [];
    return Object.entries(reportData).map(([hotelId, hotel]: [string, any]) => ({
      id: hotelId,
      data: hotel
    }));
  };

  // Get all unique dates across all rooms in a hotel
  const getUniqueDates = (hotelData: any) => {
    const datesSet = new Set<string>();
    Object.values(hotelData.rooms).forEach((room: any) => {
      Object.values(room.availability).forEach((availability: any) => {
        datesSet.add(availability.date);
      });
    });
    return Array.from(datesSet).sort();
  };

  // Get availability data for a specific room and date
  const getAvailabilityForRoomAndDate = (room: any, date: string) => {
    const availabilityData = Object.values(room.availability).find((availability: any) => availability.date === date);
    return availabilityData || { allocated: 0, reserved: 0, available: 0 };
  };

  // Format cell content for availability data
  const formatAvailabilityCell = (data: any) => {
    return `Allocated: ${data.allocated} Reserved: ${data.reserved} Available: ${data.available}`;
  };


  

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <FormControl fullWidth>
          <InputLabel id="conference-select-label">Conference</InputLabel>
          <Select
            labelId="conference-select-label"
            id="conference-select"
            value={selectedConference}
            label="Conference"
            onChange={handleConferenceChange}
          >
            {conferences.map((conference) => (
              <MenuItem key={conference.documentId} value={conference.documentId}>
                {conference.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box>
        <Button
          size={"small"}
          variant="contained"
          onClick={() => loadAvailability(selectedConference)}
          disabled={!selectedConference || loading}
          color="primary"
          title="Refresh availability data"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          {'Refresh'}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ ml: 2 }}>Loading availability data...</Typography>
        </Box>
      ) : reportData && getHotelsFromData().length > 0 ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h5" component="h3" sx={{ mb: 3 }}>
            Room Availability by Hotel
          </Typography>
          
          {getHotelsFromData().map(({ id: hotelId, data: hotelData }) => {
            const uniqueDates = getUniqueDates(hotelData);
            const rooms = Object.values(hotelData.rooms);
            
            return (
              <Paper key={hotelId} sx={{ mb: 4, p: 3 }}>
                <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
                  {hotelData.name}
                </Typography>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Room</TableCell>
                        {uniqueDates.map((date) => (
                          <TableCell key={date} sx={{ fontWeight: 'bold', minWidth: 120 }}>
                            {date}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rooms.map((room: any) => (
                        <TableRow key={room.id}>
                          <TableCell sx={{ fontWeight: 'medium' }}>
                            {room.name}
                          </TableCell>
                          {uniqueDates.map((date) => {
                            const availabilityData = getAvailabilityForRoomAndDate(room, date) as { allocated: number; reserved: number; available: number };
                            const isUnavailable = availabilityData.available < 0;
                            
                            return (
                              <TableCell 
                                key={date}
                                sx={{ 
                                  color: isUnavailable ? 'error.main' : 'inherit',
                                  fontSize: '0.875rem'
                                }}
                              >
                                Alloc: {availabilityData.allocated} <br />
                                Res: {availabilityData.reserved} <br />
                                Avail: {availabilityData.available}

                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })}
        </Box>
      ) : selectedConference && !loading ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No availability data found for the selected conference.
          </Typography>
        </Box>
      ) : null}
    </>
  );
}